/**
 * Le **letture**: la forma con cui il backend risponde a un `GET`.
 *
 * `index.ts` teneva già i corpi in ingresso — cosa il banco *manda* — e quelli
 * erano una fonte di verità sola. Le risposte no: erano dichiarate due volte,
 * una per lato, e nessuno delle due sapeva dell'altra. `SchedaAcquisto` in
 * `apps/api/src/receiving/porte.ts` e `SchedaAttesa` in
 * `apps/web/src/screens/ordini-attesi.tsx` erano la **stessa interfaccia
 * scritta due volte**: sette campi identici, mantenuti a mano. Il giorno che il
 * connettore avesse smesso di mandare `ddt`, il tipo del frontend avrebbe
 * continuato a promettere una stringa e la schermata avrebbe disegnato
 * `undefined` senza che niente protestasse — né il compilatore, né un test.
 *
 * Da qui in poi la dichiarazione è una, e serve due volte:
 *
 * - **il backend** ci tipa la porta, quindi un adattatore che smette di
 *   riempire un campo non compila;
 * - **il frontend** ci passa la risposta attraverso `leggi()`, quindi una
 *   risposta di forma diversa si ferma al confine con un messaggio, invece di
 *   entrare nella schermata come `undefined`.
 *
 * Restano fuori le viste ricavate — `VistaBanco` e le sue — che stanno in
 * `@errebi/domain` e sono già importate da entrambi i lati: là il tipo è
 * condiviso davvero, e riscriverlo qui in Zod vorrebbe dire mantenerne due.
 */
import { z } from 'zod';
import { zRuolo } from './roles.js';
/** I due reparti di personalizzazione. Erano scritti a mano in ogni rotta. */
export const zReparto = z.enum(['DTF', 'DTG', 'WEMB']);
/**
 * L'intestazione di una bolla, come la manda `GET /receiving/:postazione/attesi`.
 *
 * Sono i sette dati che l'operatrice confronta con il foglio che ha in mano:
 * il numero, chi ha spedito, il numero del DDT, il riferimento, quando è stato
 * emesso e quanto aspetta ancora. Tutto il resto è roba del catalogo, che si
 * apre **dopo** aver scelto la bolla.
 *
 * `ddt` e `riferimento` sono `null` e non assenti: la differenza fra «non c'è»
 * e «non l'abbiamo letto» va detta, non lasciata a un campo che manca.
 */
export const zSchedaAcquisto = z
    .object({
    numero: z.string(),
    fornitore: z.string(),
    /** `vendor_invoice`: il numero del documento di trasporto. */
    ddt: z.string().nullable(),
    riferimento: z.string().nullable(),
    /** Data di emissione come la scrive DecoNetwork; stringa vuota se non c'è. */
    emesso: z.string(),
    righeAttese: z.number(),
    capiAttesi: z.number(),
})
    .readonly();
/**
 * Un utente come lo vede chi amministra: **senza l'hash**, che non esce mai dal
 * servizio di accesso. Non si cancella nessuno, si disattiva: i gesti già
 * firmati devono continuare a puntare a qualcuno.
 */
export const zUtente = z
    .object({
    id: z.string(),
    login: z.string(),
    nome: z.string(),
    /** The Google account they sign in with; `null` = password only. */
    email: z.string().nullable(),
    ruoli: z.array(zRuolo).readonly(),
    attivo: z.boolean(),
    /** Entered at least once. Whoever never did can be deleted, not just deactivated. */
    entrato: z.boolean(),
})
    .readonly();
/**
 * Un'operatrice come la tiene l'ufficio.
 *
 * `login` e `utente` sono due cose diverse e vanno tenute distinte: `utente` è
 * l'**id** dell'identità collegata — quello che la tendina scrive — mentre
 * `login` è il nome con cui il timer registra il lavoro, che si legge di
 * rimando. Confonderli vorrebbe dire scrivere un login dove va un id, e
 * scollegare la persona senza che nessuno se ne accorga.
 */
export const zOperatrice = z
    .object({
    /** Opaco: lo genera l'archivio, e non cambia quando cambia il nome. */
    id: z.string(),
    nome: z.string(),
    reparto: zReparto,
    ore: z.number(),
    login: z.string().nullable(),
    utente: z.string().nullable(),
    attiva: z.boolean(),
})
    .readonly();
/**
 * Una riga del calendario di lavoro.
 *
 * **Un giorno chiuso è una riga che non c'è** (regola 34): nessun campo
 * `chiuso`, che sarebbe un secondo modo di dire la stessa cosa e che il giorno
 * dopo direbbe il contrario dell'altro.
 */
export const zTurno = z
    .object({
    /** Il numero di `Date.getDay()`: domenica è 0. */
    giorno: z.number().int().min(0).max(6),
    nome: z.string(),
    da: z.string(),
    a: z.string(),
    pausa: z.tuple([z.string(), z.string()]).readonly().nullable(),
})
    .readonly();
/**
 * La risposta allo scatto: un esito di comando che, quando accetta, dice
 * **qual è** la foto appena archiviata (regola 38-bis).
 *
 * Non è `zEsitoComando` con un campo in più per comodità: il ramo accettato di
 * questo gesto porta un'informazione che il chiamante gli ha chiesto — l'id,
 * che serve subito dopo per attaccare la foto a un fallato, e l'indirizzo, che
 * la cabina luce mette in un `<img>` senza aspettare che il catalogo si
 * aggiorni. Il ramo del rifiuto resta identico all'altro, perché un rifiuto è
 * un rifiuto e la schermata lo mostra allo stesso modo.
 */
export const zEsitoFoto = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        titolo: z.string(),
        messaggio: z.string(),
        foto: z
            .object({
            id: z.string().uuid(),
            /** Relativo: `/api/foto/«id»`. Lo compone l'API, non il frontend. */
            indirizzo: z.string(),
        })
            .readonly(),
    }),
    z.object({
        ok: z.literal(false),
        titolo: z.string(),
        motivo: z.string(),
        regola: z.string(),
    }),
]);
/**
 * Il dettaglio di una cassetta, come lo manda `GET /cassetta/:id`.
 *
 * È quello che si apre inquadrando il QR sul foglio: la stessa cosa che c'è
 * stampata, più le due che sulla carta non ci stanno — i gruppi etichettati e
 * la foto della merce — e più l'avviso che conta, `superata`.
 *
 * `superata` è il motivo per cui il QR porta l'identificativo della **scrittura**
 * e non il codice della cassetta: un foglio staccato e rimasto attaccato a una
 * cassetta che nel frattempo è stata riscritta continua a raccontare un
 * contenuto che non c'è più, ed è quello che il prototipo chiama «l'errore
 * peggiore». Con l'identificativo per scrittura, la pagina se ne accorge da sé.
 */
export const zDettaglioCassetta = z
    .object({
    cassetta: z.string(),
    ordine: z.string(),
    cliente: z.string(),
    sku: z.string(),
    articolo: z.string(),
    colore: z.string(),
    taglie: z.array(z.object({ taglia: z.string(), quante: z.number().int() }).readonly()),
    capi: z.number().int(),
    neutra: z.boolean(),
    riscritture: z.number().int(),
    /** Quando questo foglio è stato scritto, in ISO. */
    scrittaIl: z.string(),
    gruppi: z.array(z
        .object({
        id: z.string(),
        taglia: z.string(),
        quantita: z.number().int(),
    })
        .readonly()),
    /** L'indirizzo della foto della merce, quando ce n'è una. */
    foto: z.string().optional(),
    /**
     * La vista di catalogo e la famiglia dell'articolo: i due ripieghi di chi
     * una foto nostra non ce l'ha. L'elenco dei generi è ricopiato da
     * `GenereArticolo` del dominio invece che importato — questo pacchetto sta
     * al solo `zod`, perché finisce nel bundle del browser — e le cinque voci
     * vanno tenute allineate a mano.
     */
    anteprima: z.string().optional(),
    genere: z.enum(['capo', 'calzatura', 'pantalone', 'copricapo', 'accessorio']).optional(),
    /**
     * La cassetta è stata riscritta dopo questo foglio: qui c'è la scrittura
     * buona, per poterci arrivare invece di restare su una pagina che avvisa e
     * basta.
     */
    superata: z
        .object({ scrittaIl: z.string(), riscritture: z.number().int(), indirizzo: z.string() })
        .readonly()
        .nullable(),
})
    .readonly();
/**
 * Una versione di un esecutivo in archivio.
 *
 * Le versioni esistono perché DecoNetwork rigenera i file — «Production
 * settings have changed since these files were generated» — e la vecchia
 * poteva essere **già andata in macchina**. Buttarla vorrebbe dire non poter
 * più rispondere a «cosa abbiamo stampato».
 */
export const zVersioneEsecutivo = z
    .object({
    versione: z.number().int(),
    /** L'indirizzo da cui si scarica. */
    indirizzo: z.string(),
    byte: z.number().int(),
    /** sha256 dei byte, per esteso: è anche l'ETag della rotta. */
    digest: z.string(),
    /** Quando l'abbiamo preso, in ISO. */
    presoIl: z.string(),
    /** Quando è stata sostituita da una più nuova. `null` = è la corrente. */
    superataIl: z.string().nullable(),
})
    .readonly();
/**
 * Un esecutivo: il file che una macchina legge, con la sua cronologia.
 *
 * `presente: false` non è un dettaglio da nascondere, ed è il motivo per cui
 * questo campo esiste: un file che non abbiamo mai preso resta in elenco,
 * barrato. Dire «non c'è» è un'informazione; farlo sparire è una bugia, e
 * manderebbe a cercare su DecoNetwork una cosa che lì è stata cancellata.
 */
export const zEsecutivo = z
    .object({
    chiave: z.string(),
    /** Il nome del file, che è anche quello che chi stampa cerca. */
    nome: z.string(),
    /** «Fronte · Petto»: dove va, in parole. */
    dove: z.string(),
    /** «DTG», «DTF», «WEMB», «SCR»: sigla di macchina, non si traduce. */
    processo: z.string(),
    /** Quale dei tre file di DecoNetwork. */
    tipo: z.enum(['source', 'production', 'edit']),
    presente: z.boolean(),
    /** Vuote quando `presente` è falso: non l'abbiamo mai avuto. */
    versioni: z.array(zVersioneEsecutivo).readonly(),
})
    .readonly();
/**
 * Un ordine visto dall'archivio.
 *
 * Tre stati e non uno, perché sono tre situazioni che si affrontano in modo
 * diverso: `completo` non chiede niente a nessuno; `parziale` dice che qualcosa
 * si è perso ma il resto c'è; `perduto` è l'ordine per cui non possiamo fare
 * più niente — e va visto, non nascosto, perché è il costo di ogni ora in cui
 * lo scarico è stato fermo.
 *
 * `scaduto` è la riga che giustifica l'intero modulo: DecoNetwork non ha più
 * quei file, noi sì.
 */
export const zOrdineArchiviato = z
    .object({
    ordine: z.string(),
    /** Quando è partito, in ISO. `null` = ancora aperto. */
    speditoIl: z.string().nullable(),
    /** Le sigle delle macchine toccate da questo ordine. */
    lavorazioni: z.array(z.string()).readonly(),
    file: z.number().int(),
    byte: z.number().int(),
    /** L'ultimo scarico riuscito, in ISO. `null` = non ne abbiamo nessuno. */
    ultimoIl: z.string().nullable(),
    stato: z.enum(['completo', 'parziale', 'perduto']),
    /** DecoNetwork non conserva più i file di questo ordine. */
    scaduto: z.boolean(),
    esecutivi: z.array(zEsecutivo).readonly(),
})
    .readonly();
/**
 * Una segnalazione di fallato, come la legge l'ufficio (regola 8-bis).
 *
 * Le date viaggiano in ISO e non come `Date`: JSON non ha date, e un tipo che
 * promette un `Date` dopo un `JSON.parse` è una promessa che nessuno mantiene.
 * Chi rende le formatta, che è il posto in cui si sa in che fuso sta chi legge.
 *
 * `foto` è l'indirizzo e non i byte, come dappertutto: la serve una rotta sua.
 * `null` quando la webcam era guasta o nessuno ha scattato — e non è un difetto
 * del dato, è la regola 38-bis, dove la foto si chiede e non si pretende.
 *
 * `presoInCaricoDa` e `presoInCaricoIl` vanno insieme: o ci sono tutt'e due o
 * nessuno dei due. Zod non lo dice, e non vale una `refine` — chi li scrive è
 * `takeCharge` nel dominio, che li scrive insieme.
 */
export const zFallato = z
    .object({
    id: z.string(),
    /** Quando il banco l'ha segnalato, in ISO. */
    quando: z.string(),
    /** Il numero dell'ordine d'acquisto, non dell'ordine di vendita. */
    ordine: z.string(),
    riga: z.string(),
    sku: z.string(),
    articolo: z.string(),
    colore: z.string(),
    taglia: z.string(),
    quantita: z.number().int(),
    /** `/api/foto/«id»`, o `null` se non c'è nessuno scatto. */
    foto: z.string().nullable(),
    /** Il login di chi ha segnalato al banco. */
    chi: z.string(),
    presoInCaricoDa: z.string().nullable(),
    /** In ISO. `null` = ancora aperta, ed è quella che accende l'avviso. */
    presoInCaricoIl: z.string().nullable(),
})
    .readonly();
/**
 * Uno scarto di lavorazione, come lo legge l'ufficio (regola 20-quater).
 *
 * Gemello di `zFallato`, e le differenze sono le due che contano: qui c'è il
 * **reparto** — la macchina che l'ha rovinato è il primo posto dove guardare —
 * e c'è la **causale**, che al banco è obbligatoria. Non c'è la foto: la regola
 * 20-bis non la chiede, perché il capo perso non si manda a nessuno.
 *
 * Le date viaggiano in ISO come dappertutto: JSON non ha date, e un tipo che
 * promette un `Date` dopo un `JSON.parse` è una promessa che nessuno mantiene.
 */
export const zScarto = z
    .object({
    id: z.string(),
    /** Quando il reparto l'ha dichiarato, in ISO. */
    quando: z.string(),
    cassetta: z.string(),
    reparto: zReparto,
    /** L'ordine di vendita, copiato allo scarto: la cassetta poi si svuota. */
    ordine: z.string(),
    cliente: z.string(),
    articolo: z.string(),
    sku: z.string(),
    colore: z.string(),
    /** Quale taglia rifare. */
    taglia: z.string(),
    capi: z.number().int(),
    causale: z.string(),
    /** Scritta a mano, obbligatoria solo con la causale «altro». */
    spiegazione: z.string().nullable(),
    /** Chi l'ha dichiarato in reparto. */
    chi: z.string(),
    reintegratoDa: z.string().nullable(),
    /** In ISO. `null` = da rifare, ed è quello che accende l'avviso. */
    reintegratoIl: z.string().nullable(),
})
    .readonly();
/**
 * Una riga della schermata delle giacenze: lo stesso articolo secondo due fonti.
 *
 * **I `null` non sono opzionali per pigrizia.** `deco` è `null` quando
 * DecoNetwork non traccia quella variante; `magazzino` è `null` quando il WMS
 * non ne ha mai parlato — e fino all'installazione dei Modula è `null` per
 * tutti. Un `z.number()` con lo zero al posto del niente farebbe comparire uno
 * scostamento su ogni riga del catalogo, cioè un allarme che suona sempre.
 *
 * `differenza` è `deco - magazzino`, positivo quando DecoNetwork ne conta di
 * più, e `null` appena manca un lato: una sottrazione con un termine inventato
 * è un numero inventato.
 *
 * Le etichette (`articolo`, `codice`, `taglia`, `colore`) sono stringhe che
 * possono restare vuote: arrivano dal catalogo, che una CLI separata può avere
 * lasciato indietro. Una riga senza nome si mostra lo stesso, con lo SKU — è
 * più utile di una riga che non c'è.
 */
export const zGiacenza = z
    .object({
    /** `dn_sku_id`, l'unica chiave affidabile su questa istanza. */
    sku: z.string(),
    /** The catalogue `sku`, the code printed on the box. Empty when the catalogue has none. */
    skuCatalogo: z.string(),
    codice: z.string(),
    articolo: z.string(),
    colore: z.string(),
    taglia: z.string(),
    /** `inv_lvl`. `null` = DecoNetwork non conta questa variante. */
    deco: z.number().int().nullable(),
    /** `GIA_GIAC`. `null` = il magazzino non ne ha mai parlato. Non è zero. */
    magazzino: z.number().int().nullable(),
    /** Pieces on the shop shelf, from our ledger. Never `null`: the ledger is complete. */
    negozio: z.number().int(),
    differenza: z.number().int().nullable(),
    /** `inv_warn`. `null` = nessuna soglia impostata. */
    soglia: z.number().int().nullable(),
    stato: z.enum(['scostamento', 'sotto-scorta', 'solo-deco', 'solo-magazzino', 'allineato']),
})
    .readonly();
/** Una cassetta dentro una macchina: un contenitore, non un articolo. */
export const zGiacenzaCassetta = z
    .object({
    cassetta: z.string(),
    quantita: z.number().int(),
})
    .readonly();
/**
 * L'intera schermata in una risposta sola, **letture comprese**.
 *
 * `lettoIl` viaggia insieme alle righe e non in un'altra chiamata: è quel che
 * distingue un numero di adesso da uno di tre giorni fa, e una schermata che lo
 * chiedesse a parte potrebbe mostrarli disallineati proprio mentre si guarda se
 * il magazzino ha risposto.
 *
 * `null` su una delle due date vuol dire che quella fonte non ha **mai** parlato
 * — e va scritto in schermata con quelle parole, non con un trattino.
 */
export const zGiacenze = z
    .object({
    righe: z.array(zGiacenza).readonly(),
    cassette: z.array(zGiacenzaCassetta).readonly(),
    /** ISO, o `null` se non è mai stato letto. */
    decoLettoIl: z.string().nullable(),
    magazzinoLettoIl: z.string().nullable(),
})
    .readonly();
/**
 * One article inside a group box: a virtual compartment of the WMS, listed
 * inside the box it sits in and never drawn on its own.
 */
export const zScompartoFiglio = z
    .object({
    scomparto: z.number(),
    articolo: z.string(),
    descrizione: z.string(),
    /** `UBI_GIAC`: decimal on the WMS, so not forced to an integer. */
    giacenza: z.number(),
    capienza: z.number(),
})
    .readonly();
/** A physical compartment, where the WMS says it is, in mm from the back-left corner. */
export const zScompartoCassetto = z
    .object({
    scomparto: z.number(),
    /** Empty when nothing is assigned to the compartment. */
    articolo: z.string(),
    descrizione: z.string(),
    giacenza: z.number(),
    capienza: z.number(),
    x: z.number(),
    y: z.number(),
    larghezza: z.number(),
    profondita: z.number(),
    vuoto: z.boolean(),
    figli: z.array(zScompartoFiglio).readonly(),
})
    .readonly();
export const zCassettoModula = z
    .object({
    /** `UBI_UDC`. */
    cassetto: z.number(),
    pezzi: z.number(),
    numeroScomparti: z.number().int(),
    scompartiVuoti: z.number().int(),
    scomparti: z.array(zScompartoCassetto).readonly(),
})
    .readonly();
/** A machine, with the extent its compartments reach: the rectangle a drawer is drawn in. */
export const zMacchinaModula = z
    .object({
    macchina: z.number(),
    larghezza: z.number(),
    profondita: z.number(),
    cassetti: z.array(zCassettoModula).readonly(),
})
    .readonly();
/**
 * What is in every drawer of the Modula, read live from the WMS on each call.
 *
 * `lettoIl` is the time of the read; `null` = no Modula is connected, which
 * the screen says in those words rather than showing an empty warehouse.
 */
export const zCassettiModula = z
    .object({
    lettoIl: z.string().nullable(),
    macchine: z.array(zMacchinaModula).readonly(),
})
    .readonly();
/**
 * The shop shelf (rules 41–45): stock per variant, the latest movements, and
 * the Shopify lines still waiting for a variant.
 *
 * `capi` can be negative: a sale read before the load that fed it. That is a
 * true statement about the ledger, and the screen shows it rather than
 * clamping it to zero.
 */
export const zVoceScaffale = z
    .object({
    variante: z.string(),
    capi: z.number().int(),
    /** The catalogue SKU; empty when the variant has none, or is not in the catalogue. */
    sku: z.string(),
    codice: z.string(),
    articolo: z.string(),
    taglia: z.string(),
    colore: z.string(),
})
    .readonly();
export const zMovimentoScaffale = z
    .object({
    variante: z.string(),
    capi: z.number().int(),
    tipo: z.enum(['modula_pick', 'receiving', 'sale', 'refund', 'count_adjustment']),
    origine: z.string(),
    operatore: z.string().nullable(),
    /** ISO. */
    quando: z.string(),
    /** Catalogue labels, empty when the variant is not in the DecoNetwork mirror. */
    sku: z.string(),
    articolo: z.string(),
    taglia: z.string(),
    colore: z.string(),
})
    .readonly();
export const zRigaDaAbbinare = z
    .object({
    origine: z.string(),
    tipo: z.enum(['sale', 'refund']),
    sku: z.string(),
    capi: z.number().int(),
    motivo: z.enum(['senza-sku', 'sconosciuto', 'ambiguo']),
})
    .readonly();
export const zScaffale = z
    .object({
    voci: z.array(zVoceScaffale).readonly(),
    movimenti: z.array(zMovimentoScaffale).readonly(),
    daAbbinare: z.array(zRigaDaAbbinare).readonly(),
})
    .readonly();
/** Le liste, che è come queste quattro cose viaggiano davvero. */
export const zSchedeAcquisto = z.array(zSchedaAcquisto).readonly();
export const zUtenti = z.array(zUtente).readonly();
export const zOperatrici = z.array(zOperatrice).readonly();
export const zTurni = z.array(zTurno).readonly();
export const zOrdiniArchiviati = z.array(zOrdineArchiviato).readonly();
export const zFallati = z.array(zFallato).readonly();
export const zScarti = z.array(zScarto).readonly();
//# sourceMappingURL=responses.js.map